import { useEffect, useRef, useState } from 'react'

const W = 320
const PERIOD = 3.6
const VOICES = [
  { label: 'Voice A', color: '#39a0ff', freq: 5, bars: [0.4, 0.8, 0.5, 0.9, 0.3, 0.7, 0.55, 0.85, 0.45] },
  { label: 'Voice B', color: '#ff5aa0', freq: 9, bars: [0.7, 0.35, 0.85, 0.5, 0.9, 0.4, 0.75, 0.3, 0.6] },
]
const REF_N = 40
const OUT_N = 160
const outEnv = (u) => Math.exp(-((u - 0.32) ** 2) / 0.012) + 0.9 * Math.exp(-((u - 0.66) ** 2) / 0.016)

// Voice cloning via latent-space decomposition: content ("hello") is fixed
// while the active speaker vector — a fingerprint pulled from reference audio
// — is swapped, sending the same words out in a different voice.
export function Scene08Clone2D() {
  const refPathRefs = useRef([])
  const outPathRef = useRef()
  const [active, setActive] = useState(0)
  const activeRef = useRef(0)
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      // `now` can be a hair earlier than `start` on the very first frame in
      // some browsers, making t negative — clamp so idx never goes negative.
      const t = Math.max(0, (now - start) / 1000)
      const idx = Math.floor(t / PERIOD) % 2
      if (idx !== activeRef.current) {
        activeRef.current = idx
        setActive(idx)
      }

      VOICES.forEach((v, vi) => {
        let d = ''
        for (let i = 0; i <= REF_N; i++) {
          const u = i / REF_N
          const x = 12 + u * 90
          const y = vi === 0 ? 30 : 100
          const yy = y + Math.sin(u * v.freq * Math.PI * 2 - t * 4) * 12
          d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${yy.toFixed(1)} `
        }
        if (refPathRefs.current[vi]) refPathRefs.current[vi].setAttribute('d', d)
      })

      const v = VOICES[idx]
      let d = ''
      for (let i = 0; i <= OUT_N; i++) {
        const u = i / OUT_N
        const x = 20 + u * (W - 40)
        const y = 340 + outEnv(u) * Math.sin(x * v.freq * 0.3 - t * 6) * 30
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
      }
      if (outPathRef.current) {
        outPathRef.current.setAttribute('d', d)
        outPathRef.current.setAttribute('stroke', v.color)
      }

      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  return (
    <svg viewBox="0 0 320 420" style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      {VOICES.map((v, vi) => {
        const y = vi === 0 ? 30 : 100
        const isActive = active === vi
        return (
          <g key={v.label}>
            <path ref={(el) => { refPathRefs.current[vi] = el }} fill="none" stroke={v.color} strokeWidth="1.6" opacity={isActive ? 1 : 0.4} strokeLinecap="round" />
            <g transform={`translate(120, ${y})`} opacity={isActive ? 1 : 0.45}>
              {v.bars.map((h, i) => (
                <rect key={i} x={i * 9 - 4} y={-h * 22} width="7" height={h * 44} fill={v.color} />
              ))}
            </g>
            <text x={230} y={y - 26} fill={v.color} fontSize="11" fontFamily="system-ui, sans-serif">{v.label}</text>
          </g>
        )
      })}
      <text x={W / 2} y="150" fill="#5d7891" fontSize="9" fontFamily="monospace" textAnchor="middle" letterSpacing="0.06em">speaker vectors (from reference audio)</text>

      <text x="70" y="185" fill="#cfe0ec" fontSize="13" fontFamily="system-ui, sans-serif" textAnchor="middle">"hello"</text>
      <text x="70" y="200" fill="#5d7891" fontSize="9" fontFamily="monospace" textAnchor="middle" letterSpacing="0.06em">content</text>

      <text x={W / 2} y="222" fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      <rect x={W / 2 - 10} y="232" width="20" height="20" fill={VOICES[active].color} transform={`rotate(45, ${W / 2}, 242)`} />

      <text x={W / 2} y="278" fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      <path ref={outPathRef} fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <text x={W / 2} y="400" fill="#dfeaf2" fontSize="13" fontFamily="system-ui, sans-serif" textAnchor="middle">
        "hello" · <tspan fill={VOICES[active].color}>{VOICES[active].label}</tspan>
      </text>
    </svg>
  )
}
